import _ from 'lodash';
import classnames from 'classnames';
import HtmlToText from 'html-to-text';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled from 'styled-components';

import connect from 'components/lib/connect';
import BubbleContent from '../content/bubble_content';
import ConversationItem from 'models/conversation_item';
import { CustomerActivityType } from 'models/customer_activity';
import ExternalLink from 'components/lib/external_link';
import ExpandCustomerActivity from 'actions/customer/expand_customer_activity';
import { HandleHeightChange } from 'components/customer/conversation_history/virtualized/list_item';
import ItemContent from '../item_content';
import ItemMetadata from '../item_metadata';
import Linkifier from 'components/lib/linkifier';
import Communicator from 'models/communicator';
import SafeHtml from 'components/common/safe_html';

export function CustomerActivity({ className, item, shouldStartExpanded, onClick }) {
  return (
    <ItemContent item={item}>
      <ItemMetadata item={item} text={getText(item)} />
      <HandleHeightChange isManual>
        {({ onHeightChange }) => (
          <CustomerActivityContent
            className={className}
            item={item}
            onClick={onClick}
            onHeightChange={onHeightChange}
            shouldStartExpanded={shouldStartExpanded}
          />
        )}
      </HandleHeightChange>
    </ItemContent>
  );

  function getText(conversationItem) {
    if (conversationItem.initiator.type === Communicator.AGENT) {
      return 'performed an activity';
    }
    return 'activity';
  }
}

CustomerActivity.propTypes = {
  className: PropTypes.string,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  shouldStartExpanded: PropTypes.bool,
  onClick: PropTypes.func,
};

function mapStateToProps({ isFeatureEnabled }, props) {
  return {
    shouldStartExpanded:
      isFeatureEnabled('demoMode') || _.get(props, 'item.content.activityType') === CustomerActivityType.SMS,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onClick: attrs => executeAction(ExpandCustomerActivity, attrs),
  };
}

const CustomerActivityContainer = connect(mapStateToProps, mapExecuteToProps)(CustomerActivity);

export default CustomerActivityContainer;

export const StyledActivityTitle = styled.div`
  font-weight: 600;
`;

export const StyledActivityBody = styled.div`
  ${p =>
    p.isExpanded
      ? ''
      : `
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;`};
`;

export const StyledActivityExpander = styled.div`
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  border-top: 1px solid ${p => p.theme.colors.gray200};
  color: ${p => p.theme.colors.gray400};
  cursor: pointer;
  display: flex;
  justify-content: center;
  &:hover {
    background: #f9f9f9;
    color: ${p => p.theme.colors.green400};
  }
`;

export const StyledChevron = styled.div`
  cursor: pointer;
  display: inline-block;
  transition: transform 0.15s ease;
  ${p =>
    p.isDownwards &&
    `
      transform: rotate(180deg);
      `};
`;

export const StyledActivityBubbleContent = styled(BubbleContent)`
  padding: 0;
`;

export const StyledActivityContent = styled.div`
  padding: 10px 20px;
`;

export class CustomerActivityContent extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { isExpandable: false, isExpanded: props.shouldStartExpanded };

    _.bindAll(this, ['checkBodyWidth', 'expandBody']);
  }

  componentDidMount() {
    this.checkBodyWidth();
  }

  componentDidUpdate() {
    this.checkBodyWidth();
  }

  checkBodyWidth() {
    if (!this.state.isExpanded && this.bodyDiv) {
      let activityBody = this.bodyDiv;
      let isExpandable = activityBody.offsetWidth < activityBody.scrollWidth;
      if (this.state.isExpandable !== isExpandable) {
        this.setState({ isExpandable }, () => this.props.onHeightChange());
      }
    }
  }

  render() {
    const { activityType, title, body, link } = this.props.item.content;

    return (
      <StyledActivityBubbleContent
        className={classnames('CustomerActivityContent', this.props.className)}
        data-aid={`customerActivityContent-${this.props.item?.id || ''}`}
        item={this.props.item}
      >
        <StyledActivityContent>
          <bdi>
            <StyledActivityTitle className="CustomerActivityContent-title">{title}</StyledActivityTitle>
          </bdi>
          <bdi>
            <StyledActivityBody
              isExpanded={this.state.isExpanded}
              ref={div => {
                this.bodyDiv = div;
              }}
            >
              {this.renderBody(body)}
            </StyledActivityBody>
          </bdi>
          <bdi>
            {_.get(link, 'url') && (
              <ExternalLink
                subType={activityType}
                text={_.get(link, 'text')}
                type="ACTIVITY"
                url={_.get(link, 'url')}
              />
            )}
          </bdi>
        </StyledActivityContent>
        {this.renderExpander()}
      </StyledActivityBubbleContent>
    );
  }

  renderBody(body) {
    if (_.trim(HtmlToText.fromString(body)).length === _.trim(body).length) {
      return <Linkifier>{body}</Linkifier>;
    } else {
      return <SafeHtml html={body} setDirection />;
    }
  }

  renderExpander() {
    if (!this.state.isExpandable) {
      return null;
    }

    return (
      <StyledActivityExpander data-aid="expander-btn" onClick={this.expandBody}>
        <StyledChevron className="ion-chevron-up" isDownwards={!this.state.isExpanded} />
      </StyledActivityExpander>
    );
  }

  expandBody() {
    this.props.onClick({ isExpanded: this.state.isExpanded });
    this.setState({ isExpanded: !this.state.isExpanded }, () => this.props.onHeightChange());
  }
}

CustomerActivityContent.propTypes = {
  className: PropTypes.string,
  shouldStartExpanded: PropTypes.bool,
  item: PropTypes.instanceOf(ConversationItem).isRequired,
  onHeightChange: PropTypes.func,
  onClick: PropTypes.func,
};
